import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Minus, Plus, ShoppingBag, X } from 'lucide-react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { useCart } from '@/contexts/CartContext';
import { useAuth } from '@/contexts/AuthContext';
import { Separator } from './ui/separator';
import { LoginDialog } from './LoginDialog';

interface CartDrawerProps {
  children: React.ReactNode;
}

export const CartDrawer = ({ children }: CartDrawerProps) => {
  const navigate = useNavigate();
  const { cart, cartData, updateQuantity, removeFromCart, isCartOpen, setIsCartOpen } = useCart();
  const { isAuthenticated } = useAuth();
  const [showLoginDialog, setShowLoginDialog] = useState(false);

  // Helper function to get final price
  const getFinalPrice = (product: any) => {
    // console.log('Calculating final price for product:', product.total - product.base_total, product.base_total,product);
    return  parseFloat(product.base_total) + (parseFloat(product.total) - parseFloat(product.base_total));};
  
  // Helper function to get brand name
  const getBrandName = (product: any) => product.brand_label || product.brand_name || '';

  const subtotal:any = cart.reduce((sum, item) => sum + getFinalPrice(item.product) * item.quantity, 0);
  const deliveryFee = 0;
  const total:any = subtotal + deliveryFee;
const taxAmount:any = cart.reduce((sum, item) => sum + (parseFloat(item.product.total) - parseFloat(item.product.base_total)) * item.quantity, 0);
  const subToatal:any = cart.reduce((sum, item) => sum + parseFloat(item.product.base_total),0);
  const handleCheckout = () => {
    // if (!isAuthenticated) {
    //   setShowLoginDialog(true);
    //   return;
    // }
    setIsCartOpen(false);
    navigate('/cart');
  };

  return (
    <>
      {children}
      <Sheet open={isCartOpen} onOpenChange={setIsCartOpen}>
        <SheetContent className="w-full sm:max-w-lg">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <ShoppingBag className="h-5 w-5" />
            Your Cart ({cart.length} {cart.length === 1 ? 'item' : 'items'})
          </SheetTitle>
        </SheetHeader>

        <div className="flex flex-col h-full pb-6">
          {/* Cart Items */}
          <div className="flex-1 overflow-y-auto py-4">
            {cart.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <ShoppingBag className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">Your cart is empty</h3>
                <p className="text-muted-foreground mb-4">Add some products to get started</p>
                <Button onClick={() => setIsCartOpen(false)}>
                  Continue Shopping
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {cart.map((item) => {
                  const finalPrice = getFinalPrice(item.product);
                  const brandName = getBrandName(item.product);
                  
                  // Get image with proper fallback
                  const getImageSrc = () => {
                    if (item.product.base_image && item.product.base_image !== null) {
                      const baseUrl = item.product.s3_base_url || item.product.media_base_url || '';
                      return `${baseUrl}${item.product.base_image}`;
                    }
                    if (item.product.thumb_image && item.product.thumb_image !== null) {
                      const baseUrl = item.product.s3_base_url || item.product.media_base_url || '';
                      return `${baseUrl}${item.product.thumb_image}`;
                    }
                    if (item.product.default_image) {
                      if (item.product.default_image.startsWith('vendor/') || item.product.default_image.startsWith('product/')) {
                        return `https://sparts.in:8443/${item.product.default_image}`;
                      }
                      return item.product.default_image;
                    }
                    return 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23f0f0f0" width="200" height="200"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="16" dy="10.5" font-weight="bold" x="50%25" y="50%25" text-anchor="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
                  };
                  
                  const imageSrc = getImageSrc();

                  return (
                    <div key={item.product.id} className="flex gap-3 p-3 border rounded-lg">
                      <div className="w-16 h-16 bg-muted rounded-lg flex-shrink-0 overflow-hidden">
                        <img
                          src={imageSrc}
                          alt={item.product.name}
                          className="w-full h-full object-contain"
                          onError={(e) => {
                            e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23f0f0f0" width="200" height="200"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="16" dy="10.5" font-weight="bold" x="50%25" y="50%25" text-anchor="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
                          }}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-sm line-clamp-2">{item.product.name}</h4>
                        <p className="text-xs text-muted-foreground mb-2">{brandName}</p>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 border rounded-md">
                            <button
                              onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                              className="p-1 hover:bg-muted rounded-l disabled:opacity-50 disabled:cursor-not-allowed"
                              disabled={item.quantity <= 1}
                            >
                              <Minus className="h-3 w-3" />
                            </button>
                            <span className="px-2 text-sm font-medium min-w-[2rem] text-center">
                              {item.quantity}
                            </span>
                            <button
                              onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                              className="p-1 hover:bg-muted rounded-r"
                            >
                              <Plus className="h-3 w-3" />
                            </button>
                          </div>

                          <div className="text-right">
                            <p className="font-semibold">₹{parseFloat(item?.product?.base_total).toFixed(2)}</p>
                            {/* {item.product.price > finalPrice && (
                              <p className="text-xs line-through text-muted-foreground">
                                ₹{item.product.cart_item_base_total.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </p>
                            )}
                            {item.product.price > finalPrice && item.product.cart_item_total === undefined && (
                              <p className="text-xs line-through text-muted-foreground">
                                ₹{(item.product.price * item.quantity).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </p>
                            )} */}
                          </div>
                        </div>
                      </div>

                      <button
                        onClick={() => removeFromCart(item.product.id)}
                        className="p-1 hover:bg-destructive/10 hover:text-destructive rounded-full self-start transition-colors"
                        title="Remove item"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Cart Summary */}
          {cart.length > 0 && (
            <div className="border-t pt-4 space-y-4">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="font-medium">₹{parseFloat(subToatal).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">GST</span>
                  <span className="font-medium">₹{parseFloat(taxAmount).toFixed(2)}</span>
                </div>
                {/* {taxTotal > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Tax (GST)</span>
                    <span className="font-medium">₹{taxTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                )}
                {discountAmount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span className="text-muted-foreground">Discount</span>
                    <span className="font-medium">-₹{discountAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                )} */}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Delivery Fee</span>
                  <span className="font-medium">₹{deliveryFee.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
                <Separator />
                <div className="flex justify-between text-lg font-bold">
                  <span>Total</span>
                  <span>₹{parseFloat(total).toFixed(2)}</span>
                </div>
              </div>

              <Button
                className="w-full"
                size="lg"
                onClick={handleCheckout}
              >
                Proceed to Checkout
              </Button>
            </div>
          )}
        </div>
      </SheetContent>

      {/* Login Dialog */}
      <LoginDialog 
        open={showLoginDialog} 
        onOpenChange={setShowLoginDialog}
        onSuccess={() => {
          setIsCartOpen(false);
          navigate('/cart');
        }}
      />
      </Sheet>
    </>
  );
};